import { useState, MouseEvent } from 'react';
import { useRecordContext } from 'react-admin';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { JobDetailsDialog } from './JobDetailsDialog';

type DialogType = 'payload' | 'output' | null;

/**
 * A 3-dot menu component for queue job rows.
 * Provides options to view job payload and output (for finished jobs).
 */
export const JobActionsMenu = () => {
  const record = useRecordContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogType, setDialogType] = useState<DialogType>(null);

  if (!record) return null;

  const isMenuOpen = Boolean(anchorEl);

  // Check if output is available (only for finished jobs)
  const finishedStatuses = ['completed', 'failed', 'cancelled'];
  const hasOutput = finishedStatuses.includes(record.status) && record.output;

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleShowPayload = () => {
    handleMenuClose();
    setDialogType('payload');
  };

  const handleShowOutput = () => {
    handleMenuClose();
    setDialogType('output');
  };

  const handleDialogClose = () => {
    setDialogType(null);
  };

  // Get content for dialog
  const getDialogContent = () => {
    if (dialogType === 'payload') {
      return record.payload;
    }
    if (dialogType === 'output') {
      return record.output;
    }
    return null;
  };

  // Get title for dialog
  const getDialogTitle = () => {
    const process = record.process || 'Job';
    const jobId = record.id;
    if (dialogType === 'payload') {
      return `Payload: ${process} (Job ${jobId})`;
    }
    if (dialogType === 'output') {
      return `Output: ${process} (Job ${jobId})`;
    }
    return '';
  };

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        size="small"
        aria-label="job actions"
        aria-controls={isMenuOpen ? 'job-actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        id="job-actions-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'job-actions-button',
        }}
      >
        <MenuItem onClick={handleShowPayload}>Show Payload</MenuItem>
        <MenuItem onClick={handleShowOutput} disabled={!hasOutput}>
          Show Output
        </MenuItem>
      </Menu>

      <JobDetailsDialog
        open={dialogType !== null}
        onClose={handleDialogClose}
        title={getDialogTitle()}
        content={getDialogContent()}
      />
    </>
  );
};

JobActionsMenu.displayName = 'Actions';
